import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import Drawer, { useDrawerCollapsed } from './Drawer';

interface LayoutProps {
  children: React.ReactNode;
}

const LOGO_URL = 'https://edifywebsite.blob.core.windows.net/edify-files/IMG_9812.JPG';

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const user = authService.getUser();

  /* Collapse state lives here so the content column padding tracks the rail. */
  const [collapsed, toggleCollapsed] = useDrawerCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const openMobile = useCallback(() => setMobileOpen(true), []);

  const initial = user?.username?.charAt(0).toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-gradient-to-br from-bmlc-navy-50 via-white to-bmlc-navy-100">
      <Drawer
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={closeMobile}
      />

      {/* Content column — padding follows the drawer width */}
      <div
        className={`flex min-h-screen flex-col transition-[padding-left] duration-300 ease-out-soft ${
          collapsed ? 'lg:pl-[4.5rem]' : 'lg:pl-64'
        }`}
      >
        {/* Slim top bar (mobile / tablet only) */}
        <header className="glass sticky top-0 z-30 shadow-nav lg:hidden">
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
            <button
              type="button"
              onClick={openMobile}
              aria-label="Open navigation"
              aria-expanded={mobileOpen}
              className="btn-ghost flex-shrink-0 rounded-lg p-2 text-bmlc-navy-700"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <button
              type="button"
              onClick={() =>
                navigate(
                  authService.isSuperAdmin()
                    ? '/admin/dashboard'
                    : authService.isFaculty()
                    ? '/admin/documents'
                    : '/chat'
                )
              }
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-bmlc-gold-400"
            >
              <img
                src={LOGO_URL}
                alt="BM Law College Logo"
                className="h-9 w-9 flex-shrink-0 rounded-full bg-white object-contain ring-1 ring-bmlc-gold-300"
              />
              <span className="min-w-0 leading-tight">
                <span className="block truncate font-serif text-sm font-bold tracking-tight text-bmlc-navy-900 sm:text-base">
                  BM Law College
                </span>
                <span className="block truncate text-[9px] font-semibold uppercase tracking-[0.14em] text-bmlc-gold-600 sm:text-[10px]">
                  AI Legal Assistant
                </span>
              </span>
            </button>

            {user && (
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gold-sheen font-serif text-sm font-bold text-white shadow-gold-glow ring-2 ring-bmlc-gold-200 ring-offset-2 ring-offset-white">
                {initial}
              </div>
            )}
          </div>
          <div className="divider-gold my-0" />
        </header>

        {/* Main Content */}
        <main className="mx-auto w-full max-w-7xl flex-1 animate-fade-in px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
          {children}
        </main>

        {/* Footer */}
        <footer className="mt-auto pb-5 pt-2 sm:pb-6">
          <div className="mx-auto flex max-w-7xl flex-col items-center gap-2.5 px-4 sm:px-6 lg:px-8">
            <div className="divider-gold my-0 max-w-[16rem]" />
            <p className="text-[11px] tracking-wide text-gray-400 sm:text-xs">
              <span className="font-serif font-semibold text-bmlc-navy-400">BM Law College</span>
              <span className="mx-1.5 text-bmlc-gold-500">&middot;</span>
              AI Legal Assistant
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
